import { useMemo } from 'react';
import useSWR from 'swr';
import type { Units } from '@surfline/web-common';
import { fetchCameraInsights, FetchCameraInsightsParams } from 'common/api/cameras';
import useCheckCameraSupportsInsights from 'hooks/useCheckCameraSupportsInsights';
import type { Camera } from 'types/camera';
import { SpotDetails } from 'types/spot';
import { fetchCrowdForecast } from 'common/api/kbyg';
import useCameraInsightsCamera from 'hooks/useCameraInsightsCamera';
import type { FetchCrowdForecastParams } from 'types/kbygApi';
import { transformCrowdDataMiddleware } from '../utils/transformCrowdData';

const useCameraInsights = ({
  camera,
  crowdStartDate,
  endDate,
  spot,
  startDate,
  units,
}: {
  camera: Camera;
  crowdStartDate?: string | undefined;
  endDate?: number;
  spot: SpotDetails;
  startDate?: number;
  units?: Units;
}) => {
  const supportsInsights = useCheckCameraSupportsInsights(camera, spot);
  const insightsCamera = useCameraInsightsCamera({ selectedCamera: camera, spot });

  const insightsFetchParams = useMemo<FetchCameraInsightsParams | undefined>(
    () => (supportsInsights ? { cameraId: camera._id, endDate, startDate, units } : undefined),
    [camera._id, endDate, startDate, supportsInsights, units],
  );

  const {
    data: insightsData,
    error: insightsError,
    isLoading: insightsLoading,
  } = useSWR(insightsFetchParams, fetchCameraInsights, {
    errorRetryCount: 0,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
  });

  const crowdFetchParams = useMemo<FetchCrowdForecastParams | undefined>(
    () =>
      spot._id && spot.insightsCameraId
        ? {
            cameraId: insightsCamera?._id,
            spotId: spot._id,
            start: crowdStartDate,
          }
        : undefined,
    [crowdStartDate, insightsCamera, spot],
  );

  const {
    data: crowdData,
    error: crowdError,
    isLoading: crowdLoading,
  } = useSWR(crowdFetchParams, fetchCrowdForecast, {
    errorRetryCount: 0,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
    use: [transformCrowdDataMiddleware],
  });

  return {
    insights: {
      data: insightsData,
      error: insightsError,
      loading: insightsLoading,
    },
    crowd: {
      data: crowdData,
      error: crowdError,
      loading: crowdLoading,
    },
  };
};

export default useCameraInsights;
